import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import { Sidebar } from "primereact/sidebar";
import { toast } from "react-toastify";
import api from "../api/axios";
import PaymentGatewayDetails, {
  formatPaymentMethod,
} from "../components/PaymentGatewayDetails";
import {
  getOptionTemplateQuantity,
  normaliseSelectedOptions,
} from "../utils/orderItemQuantity";
import "../assets/styles/page.css";
import "../assets/styles/directory-tables.css";
import "../assets/styles/invoice-workspace.css";

import { GrPrevious, GrNext } from "react-icons/gr";
import {
  TbEyeSearch,
  TbFileInvoice,
  TbCreditCard,
  TbDownload,
} from "react-icons/tb";
import { Tag } from "primereact/tag";
import { TbPrinter, TbDeviceLaptop } from "react-icons/tb";

const PAGE_SIZE = 10;
const PAGE_NAME = "Invoices";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const invoiceStatusOptions = ["Processing", "Completed", "Failed", "Refunded"];

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = [...new Set([
    1,
    totalPages,
    currentPage,
    currentPage - 1,
    currentPage + 1,
  ])]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((left, right) => left - right);

  return pages.flatMap((page, index) =>
    index > 0 && page - pages[index - 1] > 1
      ? [`ellipsis-${page}`, page]
      : [page]
  );
};

const showRequestError = (error, fallbackMessage) => {
  // The Axios interceptor handles the global 403 popup.
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message || fallbackMessage
  );
};

const getTaxSnapshot = (invoice) => {
  let snapshot = invoice?.tax_snapshot || {};
  if (typeof snapshot === "string") {
    try {
      snapshot = JSON.parse(snapshot);
    } catch {
      snapshot = {};
    }
  }
  return snapshot;
};

const getTaxGroups = (invoice) => {
  const snapshot = getTaxSnapshot(invoice);
  return Array.isArray(snapshot.tax_groups) ? snapshot.tax_groups : [];
};

const getDisplayTaxGroups = (invoice) => {
  const groups = getTaxGroups(invoice);
  if (groups.length) return groups;
  if (invoice?.tax_type === "INTERNATIONAL" || Number(invoice?.tax_price || 0) <= 0) {
    return [];
  }

  return [{
    gst_percentage:
      Number(invoice.gst_percentage || 0) ||
      (invoice.tax_type === "CGST_SGST"
        ? Number(invoice.cgst_percentage || 0) + Number(invoice.sgst_percentage || 0)
        : Number(invoice.igst_percentage || 0)),
    cgst_percentage: Number(invoice.cgst_percentage || 0),
    sgst_percentage: Number(invoice.sgst_percentage || 0),
    tax_amount: Number(invoice.tax_price || 0),
  }];
};

const Invoices = () => {
  const [searchParams] = useSearchParams();
  const linkedInvoiceId = searchParams.get("invoice_id");
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [invoiceView, setInvoiceView] = useState("invoice");

  const [paymentStatus, setPaymentStatus] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const [zipStartDate, setZipStartDate] = useState("");
  const [zipEndDate, setZipEndDate] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 1,
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchInvoices = async (page = 1, status = paymentStatus) => {
    try {
      setLoading(true);

      const res = await api.get("/admin/invoice", {
        params: {
          page,
          limit: PAGE_SIZE,
          payment_status: status || undefined,
          search: debouncedSearch || undefined,
        },
        headers: getPermissionHeaders("view"),
      });

      const nextInvoices = res.data?.data || [];
      setInvoices(nextInvoices);
      setPagination(
        res.data?.pagination || {
          page,
          total: 0,
          totalPages: 1,
        }
      );
      return nextInvoices;
    } catch (error) {
      showRequestError(error, "Failed to fetch invoices.");
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(1, paymentStatus);
  }, [paymentStatus, debouncedSearch]);

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (value) => {
    return `₹${Number(value || 0).toFixed(2)}`;
  };

  const getCreditNotes = (invoice) => {
    return invoice?.order?.credit_notes || [];
  };

  const getLatestCreditNote = (invoice) => {
    const notes = getCreditNotes(invoice);
    return notes.length ? notes[0] : null;
  };

  const hasCreditNote = (invoice) => {
    return getCreditNotes(invoice).length > 0;
  };

  const updateInvoiceStatus = async (invoice, nextStatus) => {
    if (nextStatus === invoice.payment_status) return;

    try {
      setStatusUpdatingId(invoice.invoice_id);

      const response = await api.patch(
        `/admin/invoice/${invoice.invoice_id}/status`,
        { payment_status: nextStatus },
        { headers: getPermissionHeaders("edit") }
      );

      const updated = response.data?.data || {
        ...invoice,
        payment_status: nextStatus,
      };

      setInvoices((current) =>
        current.map((item) =>
          item.invoice_id === invoice.invoice_id
            ? { ...item, ...updated }
            : item
        )
      );
      setSelectedInvoice((current) =>
        current?.invoice_id === invoice.invoice_id
          ? { ...current, ...updated }
          : current
      );

      toast.success("Invoice status updated.");

      if (paymentStatus && paymentStatus !== nextStatus) {
        const nextPage = invoices.length === 1 && pagination.page > 1
          ? pagination.page - 1
          : pagination.page;
        await fetchInvoices(nextPage, paymentStatus);
      }
    } catch (error) {
      showRequestError(error, "Invoice status update failed.");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const openInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceView("invoice");
    setSidebarOpen(true);

    try {
      const res = await api.get(`/admin/invoice/${invoice.invoice_id}`, {
        headers: getPermissionHeaders("view"),
      });

      if (res.data?.data) {
        setSelectedInvoice((current) =>
          current?.invoice_id === invoice.invoice_id ? res.data.data : current
        );
      }
    } catch (error) {
      showRequestError(error, "Failed to load complete invoice details.");
    }
  };

  useEffect(() => {
    if (!linkedInvoiceId) return undefined;
    let active = true;

    api.get(`/admin/invoice/${linkedInvoiceId}`, {
      headers: getPermissionHeaders("view"),
    }).then((response) => {
      if (!active || !response.data?.data) return;
      setSelectedInvoice(response.data.data);
      setInvoiceView("invoice");
      setSidebarOpen(true);
    }).catch((error) => {
      if (active) showRequestError(error, "Failed to open the linked invoice.");
    });

    return () => { active = false; };
  }, [linkedInvoiceId]);

  const changeInvoiceRailPage = async (nextPage) => {
    if (
      loading ||
      nextPage < 1 ||
      nextPage > pagination.totalPages ||
      nextPage === pagination.page
    ) {
      return;
    }

    const nextInvoices = await fetchInvoices(nextPage, paymentStatus);
    if (nextInvoices.length > 0) {
      await openInvoice(nextInvoices[0]);
    }
  };

  const downloadBlob = ({ blobData, type, fileName }) => {
    const blob = new Blob([blobData], { type });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadInvoicePdf = async (invoice) => {
    try {
      setDownloadingInvoiceId(invoice.invoice_id);

      const res = await api.get(`/admin/invoice/${invoice.invoice_id}/pdf`, {
        responseType: "blob",
        headers: getPermissionHeaders("view"),
      });

      downloadBlob({
        blobData: res.data,
        type: "application/pdf",
        fileName: `${invoice.invoice_number.replace(/\//g, "-")}.pdf`,
      });

      toast.success("Invoice downloaded successfully.");
    } catch (error) {
      showRequestError(error, "PDF download failed.");
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const downloadCreditNotePdf = async (note) => {
    try {
      setDownloadingInvoiceId(`credit-${note.credit_note_id}`);

      const res = await api.get(
        `/admin/invoice/credit-note/${note.credit_note_id}/pdf`,
        {
          responseType: "blob",
          headers: getPermissionHeaders("view"),
        }
      );

      downloadBlob({
        blobData: res.data,
        type: "application/pdf",
        fileName: `${note.credit_note_number.replace(/\//g, "-")}.pdf`,
      });

      toast.success("Credit note downloaded successfully.");
    } catch (error) {
      showRequestError(error, "Credit note PDF download failed.");
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const downloadInvoiceZip = async () => {
    try {
      if (!zipStartDate || !zipEndDate) {
        toast.error("Please select start date and end date.");
        return;
      }

      setDownloadingZip(true);

      const res = await api.get("/admin/invoice/download/zip", {
        params: {
          start_date: zipStartDate,
          end_date: zipEndDate,
          payment_status: paymentStatus || undefined,
          search: debouncedSearch || undefined,
        },
        responseType: "blob",
        timeout: 0,
        headers: getPermissionHeaders("view"),
      });

      downloadBlob({
        blobData: res.data,
        type: "application/zip",
        fileName: `invoices-${zipStartDate}-to-${zipEndDate}.zip`,
      });

      toast.success("Invoices ZIP downloaded successfully.");
    } catch (error) {
      showRequestError(error, "ZIP download failed.");
    } finally {
      setDownloadingZip(false);
    }
  };

  return (
    <section className="admin-page premium-users-page">
      {(downloadingInvoiceId || downloadingZip) && (
        <div className="invoice-download-overlay">
          <div className="invoice-download-box">
            <ProgressSpinner
              style={{
                width: "60px",
                height: "60px",
              }}
              strokeWidth="4"
              animationDuration=".8s"
            />
            <h5>{downloadingZip ? "Preparing ZIP" : "Generating Invoice PDF"}</h5>
            <p>
              {downloadingZip
                ? "Please wait while we prepare all invoice documents..."
                : "Please wait while we prepare your document..."}
            </p>
          </div>
        </div>
      )}

      <div className="page-header users-hero-header">
        <div>
          <p>Billing</p>
          <h1>Invoices</h1>
        </div>
      </div>

      <div className="page-card premium-user-card">
        <div className="premium-user-toolbar">
          <div>
            <h2>Invoice Register</h2>
            <p>
              {loading
                ? "Loading invoices..."
                : `${pagination.total} invoice(s) found.`}
            </p>
          </div>

          <div className="premium-user-filters">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoice, name, email, phone..."
            />
            <select
              value={paymentStatus}
              onChange={(event) => setPaymentStatus(event.target.value)}
              aria-label="Filter invoices by status"
            >
              <option value="">All statuses</option>
              {invoiceStatusOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <div className="invoice-date-range">
              <label className="invoice-date-field">
                <span>Start date</span>
                <input
                  type="date"
                  value={zipStartDate}
                  onChange={(e) => setZipStartDate(e.target.value)}
                  placeholder="Start Date"
                />
              </label>
              <label className="invoice-date-field">
                <span>End date</span>
                <input
                  type="date"
                  value={zipEndDate}
                  onChange={(e) => setZipEndDate(e.target.value)}
                  placeholder="End Date"
                />
              </label>
            </div>
            <button
              type="button"
              className="ghost-action bottonborder"
              onClick={downloadInvoiceZip}
              disabled={downloadingZip || !!downloadingInvoiceId}
            >
              <TbDownload />
              {downloadingZip ? "Preparing ZIP..." : "Download ZIP"}
            </button>
          </div>
        </div>

        <div className={`directory-table-scroll ${loading ? "is-loading" : ""}`}>
          <table className="directory-data-table invoice-directory-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Invoice date</th>
                <th>Status</th>
                <th>Credit note</th>
                <th aria-label="Actions">Actions</th>
              </tr>
            </thead>

            <tbody>
              {!loading && invoices.length === 0 && (
                <tr>
                  <td className="directory-table-empty" colSpan="7">
                    <TbFileInvoice />
                    <strong>No invoices found</strong>
                    <span>Invoices will appear here after successful payment.</span>
                  </td>
                </tr>
              )}

              {invoices.map((invoice) => {
                const creditNote = getLatestCreditNote(invoice);
                const statusClass = String(invoice.payment_status || "processing").toLowerCase();

                return (
                  <tr
                    className={hasCreditNote(invoice) ? "invoice-credit-row" : ""}
                    key={invoice.invoice_id}
                  >
                    <td className="directory-person-cell">
                      <div className="directory-person">
                        <span className="directory-avatar"><TbFileInvoice /></span>
                        <div>
                          <strong>{invoice.invoice_number}</strong>
                          <span>Order #{invoice.order_id}</span>
                          <small>Invoice #{invoice.invoice_id}</small>
                        </div>
                      </div>
                    </td>

                    <td className="invoice-customer-cell">
                      <strong>{invoice.customer_name || "Customer"}</strong>
                      <span>{invoice.email || invoice.phone || "No contact available"}</span>
                    </td>

                    <td className="invoice-amount-cell">
                      <strong>{formatCurrency(invoice.final_price)}</strong>
                      <span>{invoice.tax_type || "Invoice total"}</span>
                    </td>

                    <td className="directory-date-cell">
                      <strong>{formatDate(invoice.invoice_date)}</strong>
                      <span>Issued {formatDate(invoice.issued_at)}</span>
                    </td>

                    <td>
                      <select
                        className={`directory-status-select status-${statusClass}`}
                        value={invoice.payment_status}
                        disabled={statusUpdatingId === invoice.invoice_id}
                        aria-label={`Change ${invoice.invoice_number} status`}
                        onChange={(event) => updateInvoiceStatus(invoice, event.target.value)}
                      >
                        {invoiceStatusOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </td>

                    <td className="invoice-credit-cell">
                      {creditNote ? (
                        <>
                          <strong>{creditNote.credit_note_number}</strong>
                          <span>
                            {creditNote.status} · {formatCurrency(creditNote.refund_amount)}
                          </span>
                        </>
                      ) : (
                        <span>No credit note</span>
                      )}
                    </td>

                    <td className="directory-actions-cell invoice-actions-cell">
                      <div className="directory-row-actions">
                        <button
                          type="button"
                          title="View invoice"
                          aria-label={`View ${invoice.invoice_number}`}
                          onClick={() => openInvoice(invoice)}
                          disabled={!!downloadingInvoiceId || downloadingZip}
                        >
                          <TbEyeSearch />
                        </button>
                        <button
                          type="button"
                          title="Download PDF"
                          aria-label={`Download ${invoice.invoice_number}`}
                          onClick={() => downloadInvoicePdf(invoice)}
                          disabled={
                            downloadingInvoiceId === invoice.invoice_id ||
                            downloadingZip
                          }
                        >
                          {downloadingInvoiceId === invoice.invoice_id ? (
                            <ProgressSpinner
                              style={{ width: "18px", height: "18px" }}
                              strokeWidth="6"
                            />
                          ) : (
                            <TbDownload />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="directory-pagination">
          <button
            type="button"
            className="directory-pagination-direction"
            disabled={pagination.page <= 1 || !!downloadingInvoiceId || downloadingZip}
            onClick={() => fetchInvoices(pagination.page - 1, paymentStatus)}
          >
            <GrPrevious /> Previous
          </button>

          <div className="directory-pagination-pages">
            {getPaginationItems(pagination.page, pagination.totalPages).map(
              (item) =>
                typeof item === "string" ? (
                  <span key={item}>…</span>
                ) : (
                  <button
                    type="button"
                    className={item === pagination.page ? "active" : ""}
                    key={item}
                    disabled={!!downloadingInvoiceId || downloadingZip || loading}
                    aria-label={`Go to page ${item}`}
                    aria-current={item === pagination.page ? "page" : undefined}
                    onClick={() => fetchInvoices(item, paymentStatus)}
                  >
                    {item}
                  </button>
                )
            )}
          </div>

          <button
            type="button"
            className="directory-pagination-direction"
            disabled={
              pagination.page >= pagination.totalPages ||
              !!downloadingInvoiceId ||
              downloadingZip
            }
            onClick={() => fetchInvoices(pagination.page + 1, paymentStatus)}
          >
            Next <GrNext />
          </button>
        </div>
      </div>

      <Sidebar
        visible={sidebarOpen}
        position="right"
        onHide={() => setSidebarOpen(false)}
        className="premium-user-sidebar customer-detail-sidebar invoice-sidebar"
      >
        {selectedInvoice && (
          <div className="invoice-admin-workspace">
            <aside className="invoice-workspace-rail" aria-label="Invoice navigation">
              <div className="invoice-rail-heading">
                <span>Invoice register</span>
                <strong>{pagination.total} documents</strong>
                <small>Page {pagination.page} of {pagination.totalPages}</small>
              </div>

              <div className="invoice-rail-list">
                {invoices.map((invoice) => (
                  <button
                    type="button"
                    key={invoice.invoice_id}
                    className={
                      selectedInvoice.invoice_id === invoice.invoice_id
                        ? "active"
                        : ""
                    }
                    aria-current={
                      selectedInvoice.invoice_id === invoice.invoice_id
                        ? "true"
                        : undefined
                    }
                    onClick={() => openInvoice(invoice)}
                  >
                    <span className="invoice-rail-icon"><TbFileInvoice /></span>
                    <span className="invoice-rail-copy">
                      <strong>{invoice.invoice_number}</strong>
                      <small>{invoice.customer_name || "Customer"}</small>
                      <small>{formatDate(invoice.invoice_date)}</small>
                    </span>
                    <span className="invoice-rail-amount">
                      <strong>{formatCurrency(invoice.final_price)}</strong>
                      <small>{invoice.payment_status}</small>
                    </span>
                  </button>
                ))}
              </div>

              <nav className="invoice-rail-pagination" aria-label="Invoice pages">
                <button
                  type="button"
                  aria-label="Previous invoice page"
                  disabled={pagination.page <= 1 || loading}
                  onClick={() => changeInvoiceRailPage(pagination.page - 1)}
                >
                  <GrPrevious />
                </button>
                <span>{pagination.page} / {pagination.totalPages}</span>
                <button
                  type="button"
                  aria-label="Next invoice page"
                  disabled={pagination.page >= pagination.totalPages || loading}
                  onClick={() => changeInvoiceRailPage(pagination.page + 1)}
                >
                  <GrNext />
                </button>
              </nav>
            </aside>

          <div className="user-view-shell invoice-drawer-shell">
            <header className="invoice-drawer-header">
              <div className="invoice-drawer-title">
                <span>Invoice details</span>
                <h2>{selectedInvoice.invoice_number}</h2>
                <p>
                  {selectedInvoice.customer_name}
                  <b aria-hidden="true">·</b>
                  Order {selectedInvoice.order?.order_number || `#${selectedInvoice.order_id}`}
                </p>
              </div>

              <button
                type="button"
                className="primary-action invoice-download-action"
                disabled={
                  downloadingInvoiceId === selectedInvoice.invoice_id ||
                  downloadingZip
                }
                onClick={() => downloadInvoicePdf(selectedInvoice)}
              >
                {downloadingInvoiceId === selectedInvoice.invoice_id ? (
                  <>
                    <ProgressSpinner
                      style={{
                        width: "18px",
                        height: "18px",
                        marginRight: "8px",
                      }}
                      strokeWidth="6"
                    />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <TbDownload />
                    Download PDF
                  </>
                )}
              </button>
            </header>

            <nav className="invoice-view-switcher" aria-label="Invoice drawer views">
              <button
                type="button"
                className={invoiceView === "invoice" ? "active" : ""}
                onClick={() => setInvoiceView("invoice")}
              >
                <TbFileInvoice />
                Invoice
              </button>
              <button
                type="button"
                className={invoiceView === "payment" ? "active" : ""}
                onClick={() => setInvoiceView("payment")}
              >
                <TbCreditCard />
                Payment
              </button>
            </nav>

            {invoiceView === "payment" ? (
              <div className="invoice-payment-view">
                <PaymentGatewayDetails
                  payment={selectedInvoice.order?.payment}
                  orderPaymentMethod={selectedInvoice.order?.payment_method}
                />

            {hasCreditNote(selectedInvoice) && (
              <div className="invoice-credit-note-panel">
                <div className="invoice-credit-note-head">
                  <div>
                    <span>Credit Note Generated</span>
                    <h3>Refund Document</h3>
                  </div>
                </div>

                {getCreditNotes(selectedInvoice).map((note) => (
                  <div
                    className="invoice-credit-note-card"
                    key={note.credit_note_id}
                  >
                    <div className="invoice-credit-note-row">
                      <strong>{note.credit_note_number}</strong>
                      <span className={`status-badge ${note.status}`}>
                        {note.status}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        margin: "10px 0",
                      }}
                    >
                      <p>
                        <strong>Refund Amount:</strong>{" "}
                        {formatCurrency(note.refund_amount)}
                      </p>

                      <p>
                        <strong>Refund Type:</strong>{" "}
                        {Number(note.refund_amount) >=
                        Number(selectedInvoice.final_price || 0)
                          ? "Full refund"
                          : "Partial refund"}
                      </p>

                      <p>
                        <strong>Remark / Against:</strong>{" "}
                        {note.reason || "-"}
                      </p>

                      <p>
                        <strong>Visible to Customer:</strong>{" "}
                        {note.is_visible ? "Yes" : "No"}
                      </p>

                      <p>
                        <strong>Cashfree Refund ID:</strong>{" "}
                        {note.cashfree_refund_id || "Refund processing"}
                      </p>
                    </div>

                    {note.status === "Processing" && (
                      <div className="invoice-credit-note-warning">
                        Cashfree is processing this refund. Status updates
                        automatically through the refund webhook.
                      </div>
                    )}

                    {note.status === "Paid" && (
                      <div className="invoice-credit-note-success">
                        Refund completed. Credit note is paid and visible to
                        customer.
                      </div>
                    )}

                    <button
                      type="button"
                      className="ghost-action bottonborder"
                      style={{ marginTop: "10px" }}
                      onClick={() => downloadCreditNotePdf(note)}
                      disabled={
                        downloadingInvoiceId === `credit-${note.credit_note_id}` ||
                        downloadingZip
                      }
                    >
                      <TbDownload />
                      Download Credit Note
                    </button>
                  </div>
                ))}
              </div>
            )}

              </div>
            ) : (
              <>

            {/* <div className="customer-restrict-box">
              <h3>Customer Details</h3>
              <p>{selectedInvoice.email}</p>
              <p>{selectedInvoice.phone}</p>
              {selectedInvoice.gst_no && <p>GST: {selectedInvoice.gst_no}</p>}
            </div>

            <div className="customer-restrict-box">
              <h3>Billing Address</h3>
              <p>
                {selectedInvoice.billing_address?.address1}
                {selectedInvoice.billing_address?.address2
                  ? `, ${selectedInvoice.billing_address.address2}`
                  : ""}
                <br />
                {selectedInvoice.billing_address?.city},{" "}
                {selectedInvoice.billing_address?.state}
                <br />
                {selectedInvoice.billing_address?.country} -{" "}
                {selectedInvoice.billing_address?.zipcode}
              </p>
            </div>

            <div className="customer-restrict-box">
              <h3>Items</h3>

              {(selectedInvoice.items || []).map((item) => {
                const snapshot = item.product_snapshot || {};

                return (
                  <>
                   <Tag
                        rounded
                        severity={snapshot.is_printable ? "info" : "danger"}
                        value={
                          <span className="d-flex align-items-center gap-3">
                            {snapshot.is_printable ? (
                              <TbPrinter size={13} />
                            ) : (
                              <TbDeviceLaptop size={13} />
                            )}
                            {snapshot.is_printable ? "Printable" : "Digital"}
                          </span>
                        }
                      />
                  <div className="admin-order-product" key={item.order_item_id}>
                    <img
                      src={snapshot.image || "/img/noimage.jpg"}
                      alt={snapshot.name || "Product"}
                    />

                    <div>
                      <strong>{snapshot.name || "Product"}</strong>
                      <p>Base Price: {formatCurrency(snapshot.final_price)}</p>
                      <p>Total: {formatCurrency(item.total_price)}</p>

                      {(item.form_values || []).map((field) => (
                        <small key={field.order_item_form_value_id}>
                          {field.field_label}:{" "}
                          {field.input_type === "file" && field.file_url ? (
                            <a
                              href={field.file_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {field.file_name || "View file"}
                            </a>
                          ) : (
                            field.value || "-"
                          )}
                        </small>
                      ))}
                    </div>
                  </div>
                  </>
                );
              })}
            </div>

            <div className="customer-restrict-box">
              <h3>Price Summary</h3>

              <table
                className="table"
                style={{
                  borderCollapse: "separate",
                  borderSpacing: "0 10px",
                  width: "100%",
                }}
              >
                <tbody>
                  <tr>
                    <td>Subtotal</td>
                    <td className="text-end">
                      {formatCurrency(selectedInvoice.subtotal)}
                    </td>
                  </tr>

                  <tr>
                    <td>Discount</td>
                    <td className="text-end text-danger">
                      - {formatCurrency(selectedInvoice.discount)}
                    </td>
                  </tr>

                  <tr>
                    <td>Platform Fee</td>
                    <td className="text-end">
                      {formatCurrency(selectedInvoice.platform_fee)}
                    </td>
                  </tr>

                  <tr>
                    <td>Shipping Fee</td>
                    <td className="text-end">
                      {formatCurrency(selectedInvoice.shipping_fee)}
                    </td>
                  </tr>

                  <tr>
                    <td>Taxable Amount</td>
                    <td className="text-end">
                      {formatCurrency(selectedInvoice.subtotal)}
                    </td>
                  </tr>

                  <tr>
                    <td>GST</td>
                    <td className="text-end">
                      {formatCurrency(selectedInvoice.tax_price)}
                    </td>
                  </tr>
                </tbody>
              </table>

              <hr />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "20px",
                }}
              >
                <b>Total</b>
                <b>{formatCurrency(selectedInvoice.final_price)}</b>
              </div>
            </div> */}

            <div className="premium-invoice-preview">
              <div className="invoice-head">
                <div>
                  <h2>INVOICE</h2>
                  <p>Graphe Weddings · Wedding Stationery</p>
                </div>

                <div className="invoice-head-right">
                  <strong>{selectedInvoice.invoice_number}</strong>
                  <span>{formatDate(selectedInvoice.invoice_date)}</span>
                  <Tag
                    value={selectedInvoice.payment_status}
                    severity={
                      selectedInvoice.payment_status === "Completed"
                        ? "success"
                        : selectedInvoice.payment_status === "Failed"
                          ? "danger"
                          : "warning"
                    }
                    rounded
                  />
                </div>
              </div>

              <div className="invoice-info-grid">
                <div>
                  <span>Billed To</span>
                  <h4>{selectedInvoice.customer_name}</h4>
                  <p>{selectedInvoice.email}</p>
                  <p>{selectedInvoice.phone}</p>
                  {selectedInvoice.gst_no && <p>GST: {selectedInvoice.gst_no}</p>}
                </div>

                <div>
                  <span>Billing Address</span>
                  <p>
                    {selectedInvoice.billing_address?.address1}
                    {selectedInvoice.billing_address?.address2
                      ? `, ${selectedInvoice.billing_address.address2}`
                      : ""}
                    <br />
                    {selectedInvoice.billing_address?.city},{" "}
                    {selectedInvoice.billing_address?.state}
                    <br />
                    {selectedInvoice.billing_address?.country} -{" "}
                    {selectedInvoice.billing_address?.zipcode}
                  </p>
                </div>

                <div>
                  <span>Payment</span>
                  <p>
                    {formatPaymentMethod(
                      selectedInvoice.order?.payment?.method ||
                        selectedInvoice.order?.payment_method
                    )}
                  </p>
                  <p>
                    Gateway: {selectedInvoice.order?.payment?.gateway || "Cashfree"}
                  </p>
                  <p>{selectedInvoice.cashfree_payment_id || "-"}</p>
                </div>

                <div>
                  <span>Order</span>
                  <p>{selectedInvoice.order?.order_number || `#${selectedInvoice.order_id}`}</p>
                  <p>{formatDate(selectedInvoice.issued_at)}</p>
                </div>
              </div>

              <div className="invoice-items-table">
                <div className="invoice-table-head">
                  <span>Item</span>
                  <span>Qty</span>
                  <span>Rate</span>
                  <span>Total</span>
                </div>

                {(selectedInvoice.items || []).map((item, itemIndex) => {
                  const snapshot = item.product_snapshot || {};
                  const snapshotTaxItems = getTaxSnapshot(selectedInvoice).items || [];
                  const itemTax =
                    item.tax ||
                    snapshotTaxItems.find(
                      (taxItem) =>
                        Number(taxItem.order_item_id) === Number(item.order_item_id)
                    ) ||
                    snapshotTaxItems[itemIndex] ||
                    {};
                  const hsnSacCode =
                    itemTax.hsn_sac_code || snapshot.hsn_sac_code || null;
                  const hasItemTax = itemTax.gst_percentage != null;
                  const selectedOptions = normaliseSelectedOptions(item.selected_options)
                    .sort(
                      (a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)
                    );
                  const displayQuantity = getOptionTemplateQuantity(item);

                  return (
                    <div className="invoice-table-row" key={item.order_item_id}>
                      <div className="invoice-product-cell">
                        <img
                          src={snapshot.image || "/img/noimage.jpg"}
                          alt={snapshot.name || "Product"}
                        />

                        <div>
                          <strong>{snapshot.name || "Product"}</strong>
                          {snapshot.sku && <p>SKU: {snapshot.sku}</p>}

                          <div className="invoice-item-tax-details">
                            <span>
                              <b>HSN/SAC:</b>{" "}
                              {hsnSacCode || "Not captured on this invoice"}
                            </span>
                            {hasItemTax ? (
                              <>
                                <span>
                                  <b>Tax:</b>{" "}
                                  {selectedInvoice.tax_type === "INTERNATIONAL"
                                    ? "Export GST 0% under LUT"
                                    : selectedInvoice.tax_type === "CGST_SGST"
                                      ? `${itemTax.gst_percentage}% GST (CGST ${itemTax.cgst_percentage}% + SGST ${itemTax.sgst_percentage}%)`
                                      : `${itemTax.gst_percentage}% IGST`}
                                </span>
                                <span>
                                  <b>Taxable value:</b>{" "}
                                  {formatCurrency(itemTax.taxable_amount)}
                                  {selectedInvoice.tax_type !== "INTERNATIONAL" && (
                                    <>
                                      {" · "}<b>Included tax:</b>{" "}
                                      {formatCurrency(itemTax.tax_amount)}
                                    </>
                                  )}
                                </span>
                              </>
                            ) : (
                              <span>
                                Item-level tax was not captured on this historical invoice.
                              </span>
                            )}
                          </div>

                          <Tag
                            rounded
                            severity={snapshot.is_printable ? "info" : "secondary"}
                            value={
                              <span className="d-flex align-items-center gap-2">
                                {snapshot.is_printable ? (
                                  <TbPrinter size={13} />
                                ) : (
                                  <TbDeviceLaptop size={13} />
                                )}
                                {snapshot.is_printable ? "Printable" : "Digital"}
                              </span>
                            }
                          />

                          {selectedOptions.length > 0 && (
                            <div className="invoice-options-list">
                              {selectedOptions.map((option, index) => (
                                <small key={index}>
                                  <b>{option.field_name}:</b>{" "}
                                  {option.label || option.value || "-"}
                                  {Number(option.price_adjustment || 0) > 0
                                    ? ` (+${formatCurrency(option.price_adjustment)})`
                                    : ""}
                                </small>
                              ))}
                            </div>
                          )}

                          {(item.form_values || []).length > 0 && (
                            <div className="invoice-options-list">
                              {(item.form_values || []).map((field) => (
                                <small key={field.order_item_form_value_id}>
                                  <b>{field.field_label}:</b>{" "}
                                  {field.input_type === "file" && field.file_url ? (
                                    <a href={field.file_url} target="_blank" rel="noreferrer">
                                      {field.file_name || "View file"}
                                    </a>
                                  ) : (
                                    field.value || "-"
                                  )}
                                </small>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <span className="invoice-row-quantity">{displayQuantity}</span>
                      <span>{formatCurrency(item.unit_price)}</span>
                      <strong>{formatCurrency(item.total_price)}</strong>
                    </div>
                  );
                })}
              </div>

              <div className="invoice-total-wrap">
                <div className="invoice-total-card">
                  <div>
                    <span>Subtotal</span>
                    <strong>{formatCurrency(selectedInvoice.subtotal)}</strong>
                  </div>

                  <div>
                    <span>Discount</span>
                    <strong>- {formatCurrency(selectedInvoice.discount)}</strong>
                  </div>

                  {selectedInvoice.taxable_amount != null && (
                    <div>
                      <span>
                        {selectedInvoice.tax_type === "INTERNATIONAL"
                          ? "Export Value"
                          : "Product Value Before Included GST"}
                      </span>
                      <strong>{formatCurrency(selectedInvoice.taxable_amount)}</strong>
                    </div>
                  )}

                  <div>
                    <span>Platform Fee</span>
                    <strong>{formatCurrency(selectedInvoice.platform_fee)}</strong>
                  </div>

                  <div>
                    <span>Shipping Fee</span>
                    <strong>{formatCurrency(selectedInvoice.shipping_fee)}</strong>
                  </div>

                  {selectedInvoice.tax_type === "INTERNATIONAL" ? (
                    <div>
                      <span>
                        International export — GST 0%
                        {getTaxSnapshot(selectedInvoice).lut_number
                          ? ` · LUT ${getTaxSnapshot(selectedInvoice).lut_number}`
                          : ""}
                      </span>
                      <strong>{formatCurrency(0)}</strong>
                    </div>
                  ) : (
                    getDisplayTaxGroups(selectedInvoice).map((group) => (
                      <div key={group.gst_percentage}>
                        <span>
                          {selectedInvoice.tax_type === "CGST_SGST"
                            ? `${group.gst_percentage}% GST included (CGST ${group.cgst_percentage}% + SGST ${group.sgst_percentage}%)`
                            : `${group.gst_percentage}% IGST included`}
                        </span>
                        <strong>{formatCurrency(group.tax_amount)}</strong>
                      </div>
                    ))
                  )}

                  <div className="grand-total">
                    <span>Total Amount</span>
                    <strong>{formatCurrency(selectedInvoice.final_price)}</strong>
                  </div>
                </div>
              </div>

              <div className="invoice-footer-note">
                <p>
                  This is a system generated invoice for your wedding stationery order.
                </p>
                {selectedInvoice.tax_type === "INTERNATIONAL" && (
                  <p>
                    {selectedInvoice.tax_snapshot?.international_tax_note}
                  </p>
                )}
              </div>
            </div>
              </>
            )}
          </div>
          </div>
        )}
      </Sidebar>

    </section>
  );
};

export default Invoices;
